import { useState } from "react";
import {
  Box, Button, TextField, Typography, Alert, Paper, Breadcrumbs, Link,
  Autocomplete, IconButton, MenuItem, useTheme, useMediaQuery,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import moment from "moment";
import { v4 as uuidv4 } from "uuid";

import InventoryIcon from "@mui/icons-material/Inventory";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import { requester } from "api/pesabackend";

const emptyLine = () => ({
  _id: uuidv4(),
  related_product: "",
  related_product_label: "",
  batch: "",
  system_quantity: "",
  counted_quantity: "",
  unit_cost: "",
});

export default function StockTakeManualForm() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isNonMobile = useMediaQuery("(min-width:600px)");
  const accent = theme.palette.secondary.main;
  const textPri = theme.palette.neutral.dark;

  const business_id = useSelector((s) => s.authReducer.business_id);
  const business_branch_id = useSelector((s) => s.authReducer.business_branch_id);

  const [countDate, setCountDate] = useState(moment().format("YYYY-MM-DD"));
  const [reference, setReference] = useState("");
  const [detail, setDetail] = useState("");
  const [lines, setLines] = useState([emptyLine()]);

  const [productOptions, setProductOptions] = useState({});
  const [batchOptions, setBatchOptions] = useState({});

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isFailed, setIsFailed] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const fetchProducts = async (lineId, search) => {
    try {
      const url = search
        ? `/inventory/products/list?business_branch=${business_branch_id}&search=${search}&typeFilter=all`
        : `/inventory/products/list?business_branch=${business_branch_id}&typeFilter=all`;
      const res = await requester.get(url);
      setProductOptions((prev) => ({ ...prev, [lineId]: res.data.data }));
    } catch {
      // silent
    }
  };

  const fetchBatches = async (lineId, productId) => {
    try {
      const res = await requester.get(`/inventory/products/batch_list/${productId}`);
      setBatchOptions((prev) => ({ ...prev, [lineId]: res.data.data }));
    } catch {
      // silent
    }
  };

  const updateLine = (lineId, field, value) => {
    setLines((prev) =>
      prev.map((l) => (l._id === lineId ? { ...l, [field]: value } : l))
    );
  };

  const addLine = () => setLines((prev) => [...prev, emptyLine()]);

  const removeLine = (lineId) =>
    setLines((prev) => prev.filter((l) => l._id !== lineId));

  const handleBatchSelect = (lineId, batchName) => {
    const batches = batchOptions[lineId] ?? [];
    const found = batches.find((b) => b.batch === batchName);
    updateLine(lineId, "batch", batchName);
    if (found) {
      updateLine(lineId, "system_quantity", found.available_stock ?? "");
    }
  };

  const validate = () => {
    if (!reference.trim()) return "Reference is required.";
    for (let i = 0; i < lines.length; i++) {
      const l = lines[i];
      if (!l.related_product) return `Line ${i + 1}: product is required.`;
      if (l.counted_quantity === "" || l.counted_quantity === null)
        return `Line ${i + 1}: counted quantity is required.`;
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { setErrorMsg(err); setIsFailed(true); return; }

    setIsSubmitting(true);
    setIsFailed(false);
    setErrorMsg("");

    const payload = {
      related_business: business_id,
      related_business_branch: business_branch_id,
      count_date: countDate,
      reference: reference.trim(),
      detail: detail.trim(),
      lines: lines.map((l) => ({
        related_product: l.related_product,
        batch: l.batch || "",
        system_quantity: l.system_quantity !== "" ? l.system_quantity : "0",
        counted_quantity: l.counted_quantity,
        unit_cost: l.unit_cost !== "" ? l.unit_cost : "0",
      })),
    };

    try {
      const res = await requester.post("/inventory/stock_count/add", JSON.stringify(payload));
      if (res.data.status === 201) {
        setIsSuccess(true);
        const sid = res.data.data?.session_id;
        setTimeout(() => {
          if (sid) {
            navigate("/stockTakeReview", { state: { sessionId: sid } });
          } else {
            navigate("/inventory");
          }
        }, 1200);
      } else {
        setIsFailed(true);
        setErrorMsg("Operation was not successful.");
      }
    } catch (ex) {
      setIsFailed(true);
      setErrorMsg(ex.response?.data?.error ?? "An unexpected error occurred.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box>
      {isSuccess && (
        <Alert sx={{ m: "15px" }} variant="filled" severity="success">
          Stock count session created! Redirecting to review…
        </Alert>
      )}
      {isFailed && (
        <Alert sx={{ m: "15px" }} variant="filled" severity="error">
          {errorMsg || "Operation was not successful."}
        </Alert>
      )}

      <Breadcrumbs sx={{ padding: "20px" }} aria-label="breadcrumb">
        <Link underline="hover" sx={{ display: "flex", alignItems: "center" }} color="inherit"
          onClick={() => navigate("/inventory")}>
          <InventoryIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          Inventory
        </Link>
        <Typography sx={{ display: "flex", alignItems: "center" }} color="text.primary">
          Manual Stock Count
        </Typography>
      </Breadcrumbs>

      {/* caveat notice */}
      <Box sx={{ mx: "20px", mb: "4px", display: "flex", alignItems: "flex-start", gap: "8px",
        p: "0.75rem 1rem", backgroundColor: "rgba(230,126,34,0.08)", border: "1px solid rgba(230,126,34,0.25)", borderRadius: "8px" }}>
        <InfoOutlinedIcon sx={{ fontSize: "16px", color: "#d35400", mt: "1px", flexShrink: 0 }} />
        <Typography sx={{ fontSize: "12px", color: "#d35400", lineHeight: 1.6 }}>
          Manual stock count records variances and posts them to the GL, but does <strong>not</strong> update
          live inventory quantities. For full inventory sync, use the file upload flow (Download Template → Upload → Process).
        </Typography>
      </Box>

      <Paper sx={{ m: "20px", p: "20px" }} elevation={2}>
        <form onSubmit={handleSubmit}>
          {/* header fields */}
          <Box sx={{ display: "grid", gap: "20px", gridTemplateColumns: isNonMobile ? "1fr 1fr 2fr" : "1fr", mb: "24px" }}>
            <TextField
              label="Count Date"
              type="date"
              size="small"
              value={countDate}
              onChange={(e) => setCountDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Reference"
              size="small"
              required
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="e.g. MANUAL-COUNT-001"
            />
            <TextField
              label="Details / Notes"
              size="small"
              value={detail}
              onChange={(e) => setDetail(e.target.value)}
            />
          </Box>

          {/* lines */}
          <Box sx={{ border: `1px solid ${theme.palette.neutral.medium ?? "rgba(0,0,0,0.12)"}`, borderRadius: "8px", p: "1rem", mb: "20px" }}>
            <Typography sx={{ fontSize: "14px", fontWeight: 600, color: textPri, mb: "12px" }}>
              Count Lines
            </Typography>

            {lines.map((line, i) => (
              <Box key={line._id} sx={{ display: "flex", alignItems: "flex-start", gap: "8px", mb: "12px", flexWrap: isNonMobile ? "nowrap" : "wrap" }}>
                <Typography sx={{ fontSize: "13px", color: theme.palette.neutral.main, pt: "8px", minWidth: "24px" }}>
                  {i + 1}.
                </Typography>

                {/* Product autocomplete */}
                <Autocomplete
                  size="small"
                  sx={{ flex: "2 1 180px", minWidth: "160px" }}
                  options={productOptions[line._id] ?? []}
                  getOptionLabel={(o) => `${o.product_name} ${o.description ?? ""}`.trim()}
                  onOpen={() => fetchProducts(line._id, null)}
                  onChange={(_, val) => {
                    updateLine(line._id, "related_product", val?.id ?? "");
                    updateLine(line._id, "related_product_label", val ? `${val.product_name} ${val.description ?? ""}`.trim() : "");
                    if (val?.id) fetchBatches(line._id, val.id);
                  }}
                  renderOption={(props, opt) => (
                    <Box component="li" {...props} key={opt.id}>
                      {opt.product_name} {opt.description}
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Product"
                      onChange={(e) => fetchProducts(line._id, e.target.value)}
                    />
                  )}
                />

                {/* Batch select */}
                <TextField
                  select
                  label="Batch"
                  size="small"
                  sx={{ flex: "1 1 120px", minWidth: "110px" }}
                  value={line.batch}
                  onChange={(e) => handleBatchSelect(line._id, e.target.value)}
                >
                  <MenuItem value="">— any —</MenuItem>
                  {(batchOptions[line._id] ?? []).map((b) => (
                    <MenuItem key={b.batch} value={b.batch}>{b.batch}</MenuItem>
                  ))}
                </TextField>

                {/* System qty (read-only populated from batch) */}
                <TextField
                  label="System Qty"
                  size="small"
                  type="number"
                  sx={{ flex: "1 1 90px", minWidth: "80px" }}
                  value={line.system_quantity}
                  onChange={(e) => updateLine(line._id, "system_quantity", e.target.value)}
                  InputProps={{ inputProps: { min: 0 } }}
                />

                {/* Counted qty */}
                <TextField
                  label="Counted Qty *"
                  size="small"
                  type="number"
                  required
                  sx={{ flex: "1 1 90px", minWidth: "80px" }}
                  value={line.counted_quantity}
                  onChange={(e) => updateLine(line._id, "counted_quantity", e.target.value)}
                  InputProps={{ inputProps: { min: 0 } }}
                />

                {/* Unit cost */}
                <TextField
                  label="Unit Cost"
                  size="small"
                  type="number"
                  sx={{ flex: "1 1 90px", minWidth: "80px" }}
                  value={line.unit_cost}
                  onChange={(e) => updateLine(line._id, "unit_cost", e.target.value)}
                  InputProps={{ inputProps: { min: 0 } }}
                />

                <IconButton
                  size="small"
                  disabled={lines.length === 1}
                  onClick={() => removeLine(line._id)}
                  sx={{ color: "#c0392b", mt: "4px" }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}

            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={addLine}
              sx={{ mt: "8px", textTransform: "none", fontSize: "13px", borderColor: accent, color: accent }}
            >
              Add Line
            </Button>
          </Box>

          {/* actions */}
          <Box sx={{ display: "flex", gap: "12px" }}>
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting}
              sx={{ fontSize: "13px", fontWeight: 500, px: "1.5rem", py: "0.5rem", borderRadius: "8px", backgroundColor: accent, color: "#fff", textTransform: "none", "&:hover": { backgroundColor: "#0F6E56" } }}
            >
              {isSubmitting ? "Saving…" : "Save Count"}
            </Button>
            <Button
              variant="outlined"
              color="error"
              disabled={isSubmitting}
              onClick={() => navigate("/inventory")}
              sx={{ fontSize: "13px", textTransform: "none", borderRadius: "8px" }}
            >
              Cancel
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
}
